import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  GenerateDynamicLinkReqProps,
  GenerateDynamicLinkResProps
} from 'services/general/general.types';
import { ResponseProps } from 'services/services.types';

import { setUserState } from 'store/signup/signupSlice';
import { clearUser, setToken } from 'store/user/userSlice';

import { formatSessionAuthorization } from 'utils/loginService';

import packageInfo from '../../../package.json';

import {
  GetAddressAutocompleteResProps,
  GetAddressAutocompleteReqProps
} from 'services/lookup/lookup.types';
import {
  ChangePasswordReqProps,
  ChangePasswordResProps,
  ChangePasswordSendCodeReqProps,
  ChangePasswordSendCodeResProps,
  ChangePasswordVerifyCodeReqProps,
  ChangePasswordVerifyCodeResProps,
  ConfirmSignInReqProps,
  ConfirmSignInResProps,
  ConfirmSignUpReqProps,
  ConfirmSignUpResProps,
  ForgotPasswordReqProps,
  ForgotPasswordResProps,
  ForgotPasswordTokenReqProps,
  ForgotPasswordTokenResProps,
  GetShortTermTokenResProps,
  LoginReqProps,
  LoginResProps,
  LogoutReqProps,
  LogoutResProps,
  PatientLoginReqProps,
  PatientLoginResProps,
  RequestVerifyIdentityCodeReqProps,
  RequestVerifyIdentityCodeResProps,
  SendVerifyEmailReqProps,
  SendVerifyEmailResProps,
  SignInWithAppleReqProps,
  SignInWithAppleResProps,
  SignInWithGoogleReqProps,
  SignInWithGoogleResProps,
  SignUpReqProps,
  SignUpResProps,
  ValidateVerifyIdentityCodeReqProps,
  ValidateVerifyIdentityCodeResProps,
  ValidateVerifyIdentityLinkReqProps,
  ValidateVerifyIdentityLinkResProps,
  VerifyUserTokenReqProps,
  VerifyUserTokenResProps
} from './auth.types';

export const authApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
      headers.set('App-Version', packageInfo.version);
      headers.set('Platform', 'web-patient');
      formatSessionAuthorization(
        headers,
        (getState() as { user?: { accessToken?: string; authSource?: string } }).user
      );
      return headers;
    }
  }),
  endpoints: (build) => ({
    // Change Password: Step 3 - Change Password
    // Note: requires same code as on Step 2.
    changePassword: build.mutation<ChangePasswordResProps, ChangePasswordReqProps>({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'PATCH',
        url: '/auth/change-password/change-password'
      })
    }),

    // Change Password: Step 1 - Send Code
    changePasswordSendCode: build.mutation<
      ChangePasswordSendCodeResProps,
      ChangePasswordSendCodeReqProps
    >({
      query: (accessToken) => ({
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/change-password/send-code'
      })
    }),

    // Change Password: Step 2 - Verify Code
    changePasswordVerifyCode: build.mutation<
      ChangePasswordVerifyCodeResProps,
      ChangePasswordVerifyCodeReqProps
    >({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/change-password/verify-code'
      })
    }),

    // Should only be called after previous IDP endpoint returned CONFIRM_SIGNIN.
    confirmSignIn: build.mutation<ConfirmSignInResProps, ConfirmSignInReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/idp/confirm-signin'
      })
    }),
    // Should only be called after previous IDP endpoint returned CONFIRM_SIGNUP
    confirmSignUp: build.mutation<ConfirmSignUpResProps, ConfirmSignUpReqProps>({
      query: (body) => ({
        body: {
          ...body,
          flowUrl: window.location.href,
          referrerUrl: window.document.referrer
        },
        method: 'POST',
        url: '/auth/idp/confirm-signup'
      })
    }),
    forgotPassword: build.mutation<ForgotPasswordResProps, ForgotPasswordReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/forgot-password'
      })
    }),
    forgotPasswordToken: build.mutation<ForgotPasswordTokenResProps, ForgotPasswordTokenReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/forgot-password-token'
      })
    }),
    // deprecated
    generateDynamicLink: build.mutation<GenerateDynamicLinkResProps, GenerateDynamicLinkReqProps>({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: '/auth/generate-dynamic-link'
      })
    }),
    getAddressAutocomplete: build.query<
      GetAddressAutocompleteResProps,
      GetAddressAutocompleteReqProps
    >({
      query: ({ search }) => ({
        params: { search },
        url: '/auth/lookup/address-autocomplete'
      })
    }),
    getShortTermToken: build.mutation<GetShortTermTokenResProps, string>({
      query: (accessToken) => ({
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/short-lived-token'
      })
    }),
    login: build.mutation<LoginResProps, LoginReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/login'
      })
    }),
    logout: build.mutation<LogoutResProps, LogoutReqProps>({
      query: (accessToken) => ({
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/logout'
      })
    }),

    patientLogin: build.mutation<PatientLoginResProps, PatientLoginReqProps>({
      query: (params) => ({
        method: 'POST',
        params,
        url: '/auth/staff/patient-login'
      })
    }),

    refreshToken: build.mutation<
      ResponseProps<{ accessToken: string; refreshToken: string }>,
      { isAuthorized: boolean; token: string }
    >({
      async onQueryStarted({ isAuthorized = true }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data) {
            dispatch(isAuthorized ? setToken(data.data) : setUserState(data.data));
          }
        } catch (_) {
          dispatch(clearUser());
          throw new Error('Error while getting refresh token');
        }
      },
      query: ({ token }) => ({
        body: { refreshToken: token },
        headers: {
          Authorization: token
        },
        method: 'POST',
        url: '/auth/token-renewal'
      })
    }),

    requestVerifyIdentityCode: build.mutation<
      RequestVerifyIdentityCodeResProps,
      RequestVerifyIdentityCodeReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/verify-identity/request-code'
      })
    }),

    /**
     * Send verify email manually.
     *  Used for:
     *  - Resend verify email.
     *  - Legacy user verify email.
     */
    resendVerifyEmail: build.mutation<SendVerifyEmailResProps, SendVerifyEmailReqProps>({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/verify-email/resend'
      })
    }),
    signInWithApple: build.mutation<SignInWithAppleResProps, SignInWithAppleReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/idp/apple'
      })
    }),
    signInWithGoogle: build.mutation<SignInWithGoogleResProps, SignInWithGoogleReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/idp/google'
      })
    }),
    signUp: build.mutation<SignUpResProps, SignUpReqProps>({
      query: (body) => ({
        body: {
          ...body,
          flowUrl: window.location.href,
          referrerUrl: window.document.referrer
        },
        method: 'POST',
        url: '/auth/freemium/sign-up'
      })
    }),
    validateVerifyIdentityCode: build.mutation<
      ValidateVerifyIdentityCodeResProps,
      ValidateVerifyIdentityCodeReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/verify-identity/validate-code'
      })
    }),
    // Exchanges the click-to-verify token for a session. POST (not GET) and body (not query) are
    // both load-bearing: a GET would be consumed by mail-client link scanners before the patient
    // clicks, and a query parameter would put the credential in the access log.
    validateVerifyIdentityLink: build.mutation<
      ValidateVerifyIdentityLinkResProps,
      ValidateVerifyIdentityLinkReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/verify-identity/validate-link'
      })
    }),
    verifyUserToken: build.mutation<VerifyUserTokenResProps, VerifyUserTokenReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/token-verify'
      })
    })
  }),
  reducerPath: 'authApi'
});

export const {
  useChangePasswordMutation,
  useChangePasswordSendCodeMutation,
  useChangePasswordVerifyCodeMutation,
  useConfirmSignInMutation,
  useConfirmSignUpMutation,
  useForgotPasswordMutation,
  useForgotPasswordTokenMutation,
  useGenerateDynamicLinkMutation,
  useGetShortTermTokenMutation,
  useLazyGetAddressAutocompleteQuery,
  useLoginMutation,
  useLogoutMutation,
  usePatientLoginMutation,
  useRefreshTokenMutation,
  useRequestVerifyIdentityCodeMutation,
  useResendVerifyEmailMutation,
  useSignInWithAppleMutation,
  useSignInWithGoogleMutation,
  useValidateVerifyIdentityCodeMutation,
  useValidateVerifyIdentityLinkMutation,
  useVerifyUserTokenMutation
} = authApi;
